import { t as uiText } from '../../i18n';
import { writeBatch, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FolderData } from '../../types/folder';
import { assertWorkspaceInvariant } from '../../utils/workspaceInvariant';

interface MovePagesToFolderParams {
  workspaceId: string;
  pageIds: string[];
  targetFolderId: string | null; // nullの場合はルートフォルダー
  folders: FolderData[];
}

/**
 * 選択されたページを指定されたフォルダーに移動する
 * @param params 移動処理のパラメータ
 * @returns Promise<{success: boolean, error?: string, ancestorFolderIds?: string[]}>
 */
export const movePagesToFolder = async (params: MovePagesToFolderParams): Promise<{success: boolean, error?: string, ancestorFolderIds?: string[]}> => {
  const { workspaceId, pageIds, targetFolderId, folders } = params;
  assertWorkspaceInvariant(workspaceId, { source: 'folder.movePagesToFolder' });

  try {
    // 各ページが存在するか事前チェック
    for (const pageId of pageIds) {
      const pageRef = doc(db, 'workspaces', workspaceId, 'boards', pageId);
      const pageDoc = await getDoc(pageRef);

      if (!pageDoc.exists()) {
        const errorMsg = uiText("Board \"{{pageId}}\" was not found", { pageId: pageId });
        console.error('❌', errorMsg);
        return { 
          success: false, 
          error: errorMsg 
        };
      }
    }

    // バッチ処理を開始
    const batch = writeBatch(db);

    // ターゲットフォルダーの祖先IDを計算：親フォルダーのancestorFolderIds + 親フォルダーのID
    const calculateAncestorIds = (folderId: string | null): string[] => {
      if (!folderId) {
        return ['root']; // ルートフォルダーの場合
      }

      const folder = folders.find(f => f.id === folderId);
      if (!folder) {
        console.warn('⚠️ フォルダーが見つからないため、ルートフォルダーとして処理:', folderId);
        return ['root']; // フォルダーが見つからない場合はルート
      }

      // 親フォルダーのancestorFolderIds + 親フォルダーのID
      const parentAncestors = folder.ancestorFolderIds || ['root'];
      return [...parentAncestors, folderId];
    };

    const ancestorIds = calculateAncestorIds(targetFolderId);

    // 各ページのfolderIdとancestorFolderIdsを更新
    for (const pageId of pageIds) {
      const pageRef = doc(db, 'workspaces', workspaceId, 'boards', pageId);

      batch.update(pageRef, {
        folderId: targetFolderId || 'root', // フォルダーID（nullの場合は'root'）
        ancestorFolderIds: ancestorIds // 祖先フォルダーIDの配列
      });
    }

    // バッチ処理を実行
    await batch.commit();

    return { 
      success: true,
      ancestorFolderIds: ancestorIds
    };
  } catch (error) {
    console.error('❌ フォルダー移動処理エラー:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : uiText("An unknown error occurred") 
    };
  }
};

/**
 * フォルダー移動処理のヘルパー関数
 * フォルダー名を取得してログ出力用に使用
 */
export const getFolderName = (folderId: string | null, folders: FolderData[]): string => {
  if (!folderId) {
    return uiText("Root folder");
  }
  
  const folder = folders.find(f => f.id === folderId);
  return folder ? folder.name || uiText("Untitled folder") : uiText("Unknown folder");
};
